import { fetchTeamMemberLookup } from '@/lib/entity-api';

export interface Vendor {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  phone: string;
}

export interface VendorMatchResult {
  matchedVendor: Vendor | null;
  confidence: number;
  matchType: 'company_name' | 'full_name' | 'partial_name' | 'none';
}

export class VendorMatchingService {
  private static instance: VendorMatchingService;
  private vendors: Vendor[] = [];
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  public static getInstance(): VendorMatchingService {
    if (!VendorMatchingService.instance) {
      VendorMatchingService.instance = new VendorMatchingService();
    }
    return VendorMatchingService.instance;
  }

  /**
   * Get all vendors from the database
   */
  public async getVendors(): Promise<Vendor[]> {
    const now = Date.now();
    
    // Return cached vendors if still fresh
    if (this.vendors.length > 0 && (now - this.lastFetch) < this.CACHE_DURATION) {
      return this.vendors;
    }

    try {
      // The company's active vendors, resolved by the backend
      const vendors = await fetchTeamMemberLookup(['vendor']);
      this.vendors = vendors.map((vendor) => ({
        id: vendor.id,
        first_name: vendor.first_name || '',
        last_name: vendor.last_name || '',
        company_name: vendor.company_name || '',
        email: vendor.email || '',
        phone: vendor.phone || ''
      }));

      this.lastFetch = now;
      return this.vendors;
    } catch (error) {
      console.error('[Vendor Matching] Failed to fetch vendors:', error);
      return [];
    }
  }

  /**
   * Match a subcontractor name from bid text to an existing vendor
   */
  public async matchVendor(extractedName: string): Promise<VendorMatchResult> {
    try {
      const vendors = await this.getVendors();
      
      if (!extractedName || vendors.length === 0) {
        return {
          matchedVendor: null,
          confidence: 0,
          matchType: 'none'
        };
      }

      const normalizedExtracted = this.normalizeName(extractedName);
      let bestMatch: Vendor | null = null;
      let bestConfidence = 0;
      let bestMatchType: 'company_name' | 'full_name' | 'partial_name' | 'none' = 'none';

      for (const vendor of vendors) {
        // Try company name match first
        if (vendor.company_name) {
          const normalizedCompany = this.normalizeName(vendor.company_name);
          const companyConfidence = this.calculateSimilarity(normalizedExtracted, normalizedCompany);
          
          if (companyConfidence > bestConfidence && companyConfidence >= 0.8) {
            bestMatch = vendor;
            bestConfidence = companyConfidence;
            bestMatchType = 'company_name';
          }
        }

        // Try full name match (first + last)
        const fullName = `${vendor.first_name} ${vendor.last_name}`.trim();
        if (fullName) {
          const normalizedFullName = this.normalizeName(fullName);
          const fullNameConfidence = this.calculateSimilarity(normalizedExtracted, normalizedFullName);
          
          if (fullNameConfidence > bestConfidence && fullNameConfidence >= 0.8) {
            bestMatch = vendor;
            bestConfidence = fullNameConfidence;
            bestMatchType = 'full_name';
          }
        }

        // Try partial name matches
        const firstNameConfidence = this.calculateSimilarity(normalizedExtracted, this.normalizeName(vendor.first_name));
        const lastNameConfidence = this.calculateSimilarity(normalizedExtracted, this.normalizeName(vendor.last_name));
        
        if (firstNameConfidence > bestConfidence && firstNameConfidence >= 0.7) {
          bestMatch = vendor;
          bestConfidence = firstNameConfidence;
          bestMatchType = 'partial_name';
        }
        
        if (lastNameConfidence > bestConfidence && lastNameConfidence >= 0.7) {
          bestMatch = vendor;
          bestConfidence = lastNameConfidence;
          bestMatchType = 'partial_name';
        }
      }

      return {
        matchedVendor: bestMatch,
        confidence: bestConfidence,
        matchType: bestMatchType
      };
    } catch (error) {
      console.error('[Vendor Matching] Error matching vendor:', error);
      return {
        matchedVendor: null,
        confidence: 0,
        matchType: 'none'
      };
    }
  }

  /**
   * Normalize a name for comparison
   */
  private normalizeName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  }

  /**
   * Calculate similarity between two strings using Levenshtein distance
   */
  private calculateSimilarity(str1: string, str2: string): number {
    if (!str1 || !str2) return 0;
    
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) {
      matrix[0][i] = i;
    }
    
    for (let j = 0; j <= str2.length; j++) {
      matrix[j][0] = j;
    }
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // deletion
          matrix[j - 1][i] + 1, // insertion
          matrix[j - 1][i - 1] + indicator // substitution
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }
}

export const vendorMatchingService = VendorMatchingService.getInstance();
